
import React, { Component } from "react";
import Form from "../common/form";
import axios from "axios";
import config from "../../config.json";
import AuthService from "../../services/authService";
import * as Yup from "yup";
import { toast } from "react-toastify";
import IconBreadcrumbs from "../custom-breadcrumb";
import { Formik } from "formik";

import {
    Box,
    Button,
    TextField,
    Typography,
    Container,
    Paper,
    InputAdornment,
} from "@mui/material";

import LockIcon from "@mui/icons-material/Lock";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import SecurityIcon from "@mui/icons-material/Security";

import ReCAPTCHA from "react-google-recaptcha";

class ChangePassword extends Form {
    apiEndPoint = config.apiEndPoint + "active-user/changepass";

    schema = Yup.object().shape({
        oldPassword: Yup.string()
            .required("Password is a required field")
            .min(4, "Minimum is 4 characters")
            .max(15, "Maximum is 15 characters"),

        newPassword: Yup.string()
            .required("Password is a required field")
            .min(4, "Minimum is 4 characters")
            .max(15, "Maximum is 15 characters"),

        confirmPassword: Yup.string().oneOf(
            [Yup.ref("newPassword"), null],
            "Passwords must match"
        ),
    });

    recaptchaRef = React.createRef();

    async doSubmit(values, captchaResponse) {
        return axios
            .post(this.apiEndPoint, {
                ...values,
                captchaResponse: captchaResponse,
            })
            .then((result) => result.data)
            .then((result) => {
                const token = result?.token;

                if (!token) {
                    toast.error("Something went wrong.");
                    return;
                }

                AuthService.storeJWT(token);

                toast.success("Password changed successfully");

                setTimeout(() => {
                    window.location = "/";
                }, 1500);
            })
            .catch((ex) => ex?.response?.data)
            .then((ex) => {
                toast.error(ex);

                this.recaptchaRef.current.reset();
            });
    }

    render() {
        return (
            <>
                <IconBreadcrumbs name={""} />

                <Formik
                    validationSchema={this.schema}
                    initialValues={{
                        oldPassword: "",
                        newPassword: "",
                        confirmPassword: "",
                    }}
                    onSubmit={(values) => {
                        this.doSubmit(
                            values,
                            this.recaptchaRef.current.getValue()
                        );
                    }}
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                    }) => (
                        <Box
                            sx={{
                                minHeight: "100vh",
                                position: "relative",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                background:
                                    "radial-gradient(circle at 50% 0%, rgba(255,70,15,0.12), transparent 38%), #070707",
                                py: { xs: 6, md: 10 },
                                px: 2,
                            }}
                        >
                            {/* TOP GOLD GLOW */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: -280,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: 700,
                                    height: 500,
                                    borderRadius: "50%",
                                    background:
                                        "radial-gradient(circle, rgba(255,70,15,0.14) 0%, transparent 70%)",
                                    filter: "blur(90px)",
                                    pointerEvents: "none",
                                }}
                            />

                            {/* LEFT GLOW */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    left: -250,
                                    top: "35%",
                                    width: 500,
                                    height: 500,
                                    borderRadius: "50%",
                                    background:
                                        "radial-gradient(circle, rgba(255,70,15,0.07) 0%, transparent 70%)",
                                    filter: "blur(100px)",
                                    pointerEvents: "none",
                                }}
                            />

                            {/* RIGHT GLOW */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    right: -250,
                                    bottom: -250,
                                    width: 550,
                                    height: 550,
                                    borderRadius: "50%",
                                    background:
                                        "radial-gradient(circle, rgba(255,70,15,0.07) 0%, transparent 70%)",
                                    filter: "blur(100px)",
                                    pointerEvents: "none",
                                }}
                            />

                            <Container
                                maxWidth="sm"
                                sx={{
                                    position: "relative",
                                    zIndex: 2,
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        position: "relative",
                                        overflow: "hidden",
                                        borderRadius: "30px",
                                        background:
                                            "linear-gradient(145deg, #11100D 0%, #090909 55%, #050505 100%)",
                                        border:
                                            "1px solid rgba(255,70,15,0.20)",
                                        boxShadow:
                                            "0 30px 100px rgba(0,0,0,0.75)",
                                    }}
                                >
                                    {/* TOP GOLD LINE */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 0,
                                            left: "8%",
                                            right: "8%",
                                            height: "2px",
                                            background:
                                                "linear-gradient(90deg, transparent, #ff7135, transparent)",
                                        }}
                                    />

                                    {/* INNER DECORATIVE BORDER */}
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: "7px",
                                            border:
                                                "1px solid rgba(255,70,15,0.07)",
                                            borderRadius: "24px",
                                            pointerEvents: "none",
                                        }}
                                    />

                                    {/* HEADER */}
                                    <Box
                                        sx={{
                                            position: "relative",
                                            textAlign: "center",
                                            px: { xs: 3, md: 5 },
                                            pt: { xs: 5, md: 6 },
                                            pb: 4,
                                            background:
                                                "linear-gradient(to bottom, rgba(255,70,15,0.08), transparent)",
                                            borderBottom:
                                                "1px solid rgba(255,70,15,0.14)",
                                        }}
                                    >
                                        {/* SECURITY ICON */}
                                        <Box
                                            sx={{
                                                width: 78,
                                                height: 78,
                                                mx: "auto",
                                                mb: 3,
                                                borderRadius: "22px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background:
                                                    "linear-gradient(145deg, #ff7135, #9f210b)",
                                                boxShadow:
                                                    "0 18px 50px rgba(255,70,15,0.20)",
                                            }}
                                        >
                                            <SecurityIcon
                                                sx={{
                                                    fontSize: 40,
                                                    color: "#111",
                                                }}
                                            />
                                        </Box>

                                        <Typography
                                            sx={{
                                                color: "#ff7135",
                                                fontSize: "11px",
                                                fontWeight: 800,
                                                letterSpacing: "5px",
                                                mb: 1.5,
                                            }}
                                        >
                                            ACCOUNT SECURITY
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color: "#fff",
                                                fontWeight: 900,
                                                fontSize: {
                                                    xs: "34px",
                                                    md: "46px",
                                                },
                                                lineHeight: 1,
                                                letterSpacing: "-1px",
                                            }}
                                        >
                                            CHANGE{" "}
                                            <span
                                                style={{
                                                    color: "#ff7135",
                                                }}
                                            >
                                                PASSWORD
                                            </span>
                                        </Typography>

                                        <Typography
                                            sx={{
                                                color:
                                                    "rgba(255,255,255,0.55)",
                                                mt: 2,
                                                fontSize: "14px",
                                                lineHeight: 1.8,
                                            }}
                                        >
                                            Protect your Vex account
                                            <br />
                                            and keep your warrior secure.
                                        </Typography>
                                    </Box>

                                    {/* CONTENT */}
                                    <Box
                                        sx={{
                                            p: {
                                                xs: 3,
                                                md: 5,
                                            },
                                        }}
                                    >
                                        <form
                                            noValidate
                                            onSubmit={handleSubmit}
                                        >
                                            {/* FORM LABEL */}
                                            <Typography
                                                sx={{
                                                    color: "#ff7135",
                                                    fontSize: "11px",
                                                    fontWeight: 800,
                                                    letterSpacing: "3px",
                                                    mb: 2.5,
                                                }}
                                            >
                                                SECURITY SETTINGS
                                            </Typography>

                                            {/* OLD PASSWORD */}
                                            <Box sx={{ mb: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    type="password"
                                                    name="oldPassword"
                                                    label="Current Password"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={
                                                        values.oldPassword
                                                    }
                                                    variant="outlined"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <LockIcon
                                                                    sx={{
                                                                        color:
                                                                            "#ff7135",
                                                                    }}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root":
                                                        {
                                                            color: "#fff",
                                                            background:
                                                                "rgba(255,255,255,0.025)",
                                                            borderRadius:
                                                                "15px",
                                                            transition:
                                                                "0.25s",

                                                            "& fieldset":
                                                            {
                                                                borderColor:
                                                                    "rgba(255,255,255,0.10)",
                                                            },

                                                            "&:hover":
                                                            {
                                                                background:
                                                                    "rgba(255,70,15,0.035)",
                                                            },

                                                            "&:hover fieldset":
                                                            {
                                                                borderColor:
                                                                    "rgba(255,70,15,0.45)",
                                                            },

                                                            "&.Mui-focused":
                                                            {
                                                                background:
                                                                    "rgba(255,70,15,0.04)",
                                                            },

                                                            "&.Mui-focused fieldset":
                                                            {
                                                                borderColor:
                                                                    "#ff7135",
                                                            },
                                                        },

                                                        "& .MuiInputLabel-root":
                                                        {
                                                            color:
                                                                "rgba(255,255,255,0.45)",
                                                        },

                                                        "& .MuiInputLabel-root.Mui-focused":
                                                        {
                                                            color:
                                                                "#ff7135",
                                                        },

                                                        "& input":
                                                        {
                                                            color: "#fff",
                                                            padding:
                                                                "17px 14px",
                                                        },
                                                    }}
                                                />

                                                {errors.oldPassword &&
                                                    touched.oldPassword && (
                                                        <Typography
                                                            sx={{
                                                                mt: 1.2,
                                                                ml: 1,
                                                                color:
                                                                    "#ff5c5c",
                                                                fontSize:
                                                                    "12px",
                                                            }}
                                                        >
                                                            {
                                                                errors.oldPassword
                                                            }
                                                        </Typography>
                                                    )}
                                            </Box>

                                            {/* NEW PASSWORD */}
                                            <Box sx={{ mb: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    type="password"
                                                    name="newPassword"
                                                    label="New Password"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={
                                                        values.newPassword
                                                    }
                                                    variant="outlined"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <VpnKeyIcon
                                                                    sx={{
                                                                        color:
                                                                            "#ff7135",
                                                                    }}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root":
                                                        {
                                                            color: "#fff",
                                                            background:
                                                                "rgba(255,255,255,0.025)",
                                                            borderRadius:
                                                                "15px",
                                                            transition:
                                                                "0.25s",

                                                            "& fieldset":
                                                            {
                                                                borderColor:
                                                                    "rgba(255,255,255,0.10)",
                                                            },

                                                            "&:hover":
                                                            {
                                                                background:
                                                                    "rgba(255,70,15,0.035)",
                                                            },

                                                            "&:hover fieldset":
                                                            {
                                                                borderColor:
                                                                    "rgba(255,70,15,0.45)",
                                                            },

                                                            "&.Mui-focused":
                                                            {
                                                                background:
                                                                    "rgba(255,70,15,0.04)",
                                                            },

                                                            "&.Mui-focused fieldset":
                                                            {
                                                                borderColor:
                                                                    "#ff7135",
                                                            },
                                                        },

                                                        "& .MuiInputLabel-root":
                                                        {
                                                            color:
                                                                "rgba(255,255,255,0.45)",
                                                        },

                                                        "& .MuiInputLabel-root.Mui-focused":
                                                        {
                                                            color:
                                                                "#ff7135",
                                                        },

                                                        "& input":
                                                        {
                                                            color: "#fff",
                                                            padding:
                                                                "17px 14px",
                                                        },
                                                    }}
                                                />

                                                {errors.newPassword &&
                                                    touched.newPassword && (
                                                        <Typography
                                                            sx={{
                                                                mt: 1.2,
                                                                ml: 1,
                                                                color:
                                                                    "#ff5c5c",
                                                                fontSize:
                                                                    "12px",
                                                            }}
                                                        >
                                                            {
                                                                errors.newPassword
                                                            }
                                                        </Typography>
                                                    )}
                                            </Box>

                                            {/* CONFIRM PASSWORD */}
                                            <Box sx={{ mb: 3 }}>
                                                <TextField
                                                    fullWidth
                                                    type="password"
                                                    name="confirmPassword"
                                                    label="Confirm New Password"
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    value={
                                                        values.confirmPassword
                                                    }
                                                    variant="outlined"
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <SecurityIcon
                                                                    sx={{
                                                                        color:
                                                                            "#ff7135",
                                                                    }}
                                                                />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        "& .MuiOutlinedInput-root":
                                                        {
                                                            color: "#fff",
                                                            background:
                                                                "rgba(255,255,255,0.025)",
                                                            borderRadius:
                                                                "15px",
                                                            transition:
                                                                "0.25s",

                                                            "& fieldset":
                                                            {
                                                                borderColor:
                                                                    "rgba(255,255,255,0.10)",
                                                            },

                                                            "&:hover":
                                                            {
                                                                background:
                                                                    "rgba(255,70,15,0.035)",
                                                            },

                                                            "&:hover fieldset":
                                                            {
                                                                borderColor:
                                                                    "rgba(255,70,15,0.45)",
                                                            },

                                                            "&.Mui-focused":
                                                            {
                                                                background:
                                                                    "rgba(255,70,15,0.04)",
                                                            },

                                                            "&.Mui-focused fieldset":
                                                            {
                                                                borderColor:
                                                                    "#ff7135",
                                                            },
                                                        },

                                                        "& .MuiInputLabel-root":
                                                        {
                                                            color:
                                                                "rgba(255,255,255,0.45)",
                                                        },

                                                        "& .MuiInputLabel-root.Mui-focused":
                                                        {
                                                            color:
                                                                "#ff7135",
                                                        },

                                                        "& input":
                                                        {
                                                            color: "#fff",
                                                            padding:
                                                                "17px 14px",
                                                        },
                                                    }}
                                                />

                                                {errors.confirmPassword &&
                                                    touched.confirmPassword && (
                                                        <Typography
                                                            sx={{
                                                                mt: 1.2,
                                                                ml: 1,
                                                                color:
                                                                    "#ff5c5c",
                                                                fontSize:
                                                                    "12px",
                                                            }}
                                                        >
                                                            {
                                                                errors.confirmPassword
                                                            }
                                                        </Typography>
                                                    )}
                                            </Box>

                                            {/* CAPTCHA */}
                                            <Box
                                                sx={{
                                                    mb: 4,
                                                    mt: 4,
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    "& > div": {
                                                        maxWidth: "100%",
                                                    },
                                                }}
                                            >
                                                <ReCAPTCHA
                                                    ref={this.recaptchaRef}
                                                    sitekey={
                                                        config.RECAPTCHA_SITE_KEY
                                                    }
                                                />
                                            </Box>

                                            {/* BUTTON */}
                                            <Button
                                                fullWidth
                                                type="submit"
                                                sx={{
                                                    py: 1.7,
                                                    borderRadius: "15px",
                                                    fontWeight: 900,
                                                    fontSize: "14px",
                                                    letterSpacing: "1.5px",
                                                    color: "#111",
                                                    background:
                                                        "linear-gradient(135deg, #ff7135 0%, #ff5420 50%, #9f210b 100%)",
                                                    boxShadow:
                                                        "0 12px 30px rgba(255,70,15,0.18)",
                                                    transition: "0.25s",

                                                    "&:hover":
                                                    {
                                                        background:
                                                            "linear-gradient(135deg, #ffb05b 0%, #ff7135 50%, #ff5420 100%)",
                                                        boxShadow:
                                                            "0 16px 40px rgba(255,70,15,0.28)",
                                                        transform:
                                                            "translateY(-2px)",
                                                    },

                                                    "&:active":
                                                    {
                                                        transform:
                                                            "translateY(0)",
                                                    },
                                                }}
                                            >
                                                UPDATE PASSWORD
                                            </Button>

                                            {/* SECURITY NOTE */}
                                            <Box
                                                sx={{
                                                    mt: 4,
                                                    p: 2,
                                                    borderRadius: "14px",
                                                    background:
                                                        "rgba(255,70,15,0.04)",
                                                    border:
                                                        "1px solid rgba(255,70,15,0.08)",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color:
                                                            "rgba(255,255,255,0.40)",
                                                        fontSize: "11px",
                                                        lineHeight: 1.7,
                                                    }}
                                                >
                                                    Your password should be kept
                                                    private and never shared
                                                    with anyone.
                                                </Typography>
                                            </Box>
                                        </form>
                                    </Box>

                                    {/* BOTTOM BRANDING */}
                                    <Box
                                        sx={{
                                            px: 3,
                                            py: 2.5,
                                            textAlign: "center",
                                            borderTop:
                                                "1px solid rgba(255,255,255,0.05)",
                                            background:
                                                "rgba(0,0,0,0.25)",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                color:
                                                    "rgba(255,255,255,0.25)",
                                                fontSize: "10px",
                                                fontWeight: 700,
                                                letterSpacing: "3px",
                                                textTransform: "uppercase",
                                            }}
                                        >
                                            S165 • CLASSIC CONQUER • 1.0
                                        </Typography>
                                    </Box>
                                </Paper>
                            </Container>
                        </Box>
                    )}
                </Formik>
            </>
        );
    }
}

export default ChangePassword;

